import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowRight, Sparkles, Target, Lightbulb, FileText } from 'lucide-react';

export default function HomePage() {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "AI-Powered Match Scoring",
      description: "Get an instant score showing how well your resume matches the job requirements."
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Skills Gap Analysis",
      description: "Identify missing skills and keywords that could be costing you interviews."
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: "Actionable Suggestions",
      description: "Receive specific, prioritized recommendations to improve your resume."
    }
  ];

  const stats = [
    { value: "85%", label: "Higher response rate" },
    { value: "50+", label: "Keywords analyzed" },
    { value: "30s", label: "Average analysis time" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="hero-pattern py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-lime-100 text-lime-700 border-lime-200">
                AI-Powered Resume Analysis
              </Badge>
              
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight">
                Land More Interviews with a Perfect Resume Match
              </h1>
              
              <p className="text-xl text-slate-600 max-w-lg">
                Upload your resume, paste the job description, and get instant AI-powered 
                analysis with actionable insights to beat the ATS.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={isAuthenticated ? "/app/analyze" : "/signup"}>
                  <Button 
                    size="lg" 
                    className="bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg transition-all text-lg px-8 h-14"
                    data-testid="cta-button"
                  >
                    {isAuthenticated ? "Start Analysis" : "Get Started Free"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 text-lg px-8 h-14"
                  >
                    View Pricing
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-8 pt-4">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="font-heading font-bold text-2xl text-slate-900">{stat.value}</div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1758876020343-c8c2add9d527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjByZXZpZXdpbmclMjBkb2N1bWVudHxlbnwwfHx8fDE3NzAyNDYwNjB8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional reviewing document"
                className="rounded-2xl shadow-[0_24px_48px_rgba(15,23,42,0.16)] w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Everything you need to optimize your resume
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our AI analyzes your resume against the job description and provides 
              comprehensive feedback to help you stand out.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <Card key={i} className="bg-slate-50 border-0 card-hover">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-slate-600">
              Get your resume analyzed in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Upload Resume", desc: "Upload your resume in PDF, DOCX, or TXT format" },
              { step: "2", title: "Paste Job Description", desc: "Copy and paste the job posting you're targeting" },
              { step: "3", title: "Get Analysis", desc: "Receive detailed analysis with match score and suggestions" }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-heading font-bold">
                    {item.step}
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block flex-1 h-px bg-slate-300" />
                  )}
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Ready to optimize your resume?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join thousands of job seekers who have improved their interview rates with JobMatch.
          </p>
          <Link to={isAuthenticated ? "/app/analyze" : "/signup"}>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 h-14"
            >
              {isAuthenticated ? "Start Analysis" : "Start Free Trial"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-slate-400 mt-4 text-sm">
            No credit card required • 3 free analyses
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
